import React, { useEffect, useState } from "react";
import { dateFormator, getEmployeeId, mapTableRows } from "../../../../utils";
import {
  Backdrop,
  Button,
  DocumentsList,
  LogoLoader,
  Modal,
  TableAdv,
  showToast,
  StatusTags,
  SmartTooltip,
} from "../../../../components";
import { useDispatch, useSelector } from "react-redux";
import {
  clearTimesheetData,
  getTimesheetsStart,
  updateTimesheetStart,
} from "../../../../redux/slices/timesheets/timsheetSlice";
import { usePagination } from "../../../../hooks";
import { getEmployeeName } from "../../../../utils/getDisplays";
import { Link } from "react-router-dom";
import CustomSection from "../../../../components/Common/CustomSection";
import { useLoginUser } from "../../../../hooks/useLoginUser";
import { exactUTCDate } from "../../../../utils/dateFormator";
import { TimesheetStatus } from "../../../../assets/data/enumData";
import { setEmployeeProfileUiid } from "../../../../redux/slices/employee/employeeSlice";

const MyPendingTimesheets = () => {
  const dispatch = useDispatch();
  const { userData: loginUserData } = useLoginUser();
  const loginUserUiid = loginUserData?.uiid;
  const [approveModal, setApproveModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [timesheetsData, setTimesheetsData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const { loading, error, timesheets, totalCount } = useSelector(
    (state) => state.timesheet
  );
  const { page, limit, setPage, handleLimitChange } = usePagination(10);

  const handelDocumetsModal = (documents) => {
    setSelectedDocuments(documents);
    setIsModalOpen(true);
  };
  useEffect(() => {
    const fetchTimesheetsData = async () => {
      if (!Array.isArray(timesheets?.data)) return;

      const data = await Promise.all(
        timesheets?.data?.map(async (row) => ({
          id: row?.uiid || "-", // Important: Add id field for TableAdv
          placementId: row?.placementId?.uiid || "-",
          employeeName: row?.employeeId
            ? `${row.employeeId.firstName || ""} ${
                row.employeeId.lastName || ""
              }`
            : "-",
          fromDate: row?.period?.from || "-",
          toDate: row?.period?.to || "-",
          submittedBy: row?.auditHistory
            .slice()
            .reverse()
            .find((history) => history.status === 2)?.updatedBy
            ? `${
                row?.auditHistory
                  ?.slice()
                  ?.reverse()
                  ?.find((history) => history.status === 2)?.updatedBy
                  ?.firstName || ""
              } ${
                row?.auditHistory
                  ?.slice()
                  ?.reverse()
                  ?.find((history) => history.status === 2)?.updatedBy
                  ?.lastName || ""
              }`
            : "-",
          submittedOn:
            row?.auditHistory
              .slice()
              .reverse()
              .find((history) => history.status === 2)?.timestamp || "-",
          totalHours: row?.dailyEntries
            ?.reduce((acc, curr) => acc + curr.st, 0)
            .toFixed(2),
          totalBillableHours: row?.dailyEntries
            ?.reduce((acc, curr) => acc + curr.tbh, 0)
            .toFixed(2),
          client: row?.clientId?.businessName || "-",
          status: row?.status || "-",
          timesheetFrequency:
            row?.placementId?.timesheetDetails?.submissionSettings?.cycles ||
            "-",
          employeeId: row?.employeeId?.uiid || "-",
          employementType: row?.employeeId?.employmentType || "-",
          uiid: row?.uiid || "-",
          attachments: row?.attachments || [],
        }))
      );

      setTimesheetsData(data?.length ? data : [] || []);
    };
    fetchTimesheetsData();
  }, [timesheets]);

  const columns = [
    {
      key: "employeeId",
      label: "Employee ID",
      render: (row) => getEmployeeId(row.employeeId, row.employementType),
    },
    {
      key: "employeeName",
      label: "Employee Name",
      width:"min-w-[250px] max-w-[300px]",
      render: (row) => {
        return (
          <SmartTooltip
            description={row.employeeName}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <div className="flex items-center gap-1">
              <span className="text-linkblue line-clamp-1">
                <Link
                  to="/employee/employee-profile/timesheet"
                  onClick={() =>
                    dispatch(setEmployeeProfileUiid(row.employeeId))
                  }
                  state={{
                    uiid: row?.placementId,
                    timesheetId: row?.uiid,
                    employeeId: row.employeeId,
                  }}
                >
                  {row.employeeName || "-"}
                </Link>
              </span>
            </div>
          </SmartTooltip>
        );
      },
    },
    {
      key: "client",
      label: "Client",
      width:"min-w-[250px] max-w-[300px]",
      render: (row) => (
        <SmartTooltip
          description={row.client}
          size="xs"
          color="black"
          noicon
          tooltipPosition="bottom"
          portal={true}
        >
          <div className="line-clamp-1">{row.client}</div>
        </SmartTooltip>
      ),
    },
    {
      key: "fromDate",
      label: "From Date",
      render: (row) => exactUTCDate(row.fromDate),
    },
    {
      key: "toDate",
      label: "To Date",
      render: (row) => exactUTCDate(row.toDate),
    },
    {
      key: "submittedBy",
      label: "Submitted By",
      render: (row) => {
        const submittedBy =
          typeof row.submittedBy === "undefined" ||
          row.submittedBy === "undefined undefined" ||
          row.submittedBy === null ||
          row.submittedBy === ""
            ? "-"
            : row.submittedBy;
        return submittedBy === "undefined" ? "-" : submittedBy;
      },
    },
    {
      key: "totalHours",
      label: "Total Hours",
    },
    {
      key: "totalBillableHours",
      label: "Total Billable Hours",
    },
    {
      key: "submittedOn",
      label: "Submitted On",
      render: (row) => dateFormator(row.submittedOn, "month-day-year"),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => {
        return (
          <StatusTags
            tag={
              TimesheetStatus?.find((status) => status.value === row.status)
                ?.label || "-"
            }
          />
        );
      },
      sortable: false,
    },
    {
      key: "attachments",
      label: "Attachments",
      render: (row) => {
        const attachments = row?.attachments || [];
        const updateAttach = attachments.map((data) => {
          return {
            filePath: data,
            name: "timeSheet",
          };
        });
        return (
          <div
            className={`flex items-center gap-1 flex-row ${
              updateAttach.length > 0 && "cursor-pointer"
            }`}
            onClick={
              updateAttach.length > 0
                ? () => handelDocumetsModal(updateAttach)
                : () => {}
            }
          >
            <span className={`${updateAttach.length > 0 && "text-linkblue"}`}>
              {updateAttach.length} Docs{" "}
            </span>
            <div>
              <Button
                type="icon"
                icon="docs"
                size="xs"
                variant="text"
                color="black"
              />
            </div>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    dispatch(
      getTimesheetsStart({
        page: page,
        limit: limit,
        filters: {
          status: {
            type: "IN",
            value: [1, 2],
          },
        },
        sort: { updatedAt: -1 },
        ref: "Employee Client PlacementWithApprovers UpdatedBy",
        incl: [
          "employmentType firstName lastName",
          "uiid businessName",
          "uiid timesheetDetails.approvers",
        ],
      })
    );
    return () => {
      dispatch(clearTimesheetData());
    };
  }, [page, limit]);

  const mappedRows = mapTableRows(timesheetsData, columns);

  const handleSelectionChange = (selectedRowIds) => {
    const selectedRowObjects = mappedRows.filter((row) =>
      selectedRowIds.includes(row.id)
    );

    const timesheetIds = selectedRowObjects.map((row) => row.uiid);

    setSelectedEmployeeIds(timesheetIds);
  };

  const handleSave = (statusType) => {
    if (selectedEmployeeIds.length > 0) {
      selectedEmployeeIds.forEach((id) => {
        // to get the timesheet data of selected ids
        const filteredData = timesheets?.data?.find((item) => item.uiid === id);

        const isUserlastApprover =
          filteredData?.placementId?.timesheetDetails?.approvers?.slice(-1)[0]
            ?.id?.userId === loginUserUiid;
        dispatch(
          updateTimesheetStart({
            coll: "Timesheet",
            uiid: id,
            payload: [
              {
                type: "SET",
                value: {
                  //to keep last approvers status as actual timesheet status
                  ...(isUserlastApprover && { status: statusType }),
                },
              },
              {
                type: "PUSH",
                value: {
                  ...(statusType && {
                    auditHistory: [
                      {
                        timestamp: new Date(),
                        status: statusType,
                        st: filteredData?.dailyEntries?.reduce(
                          (acc, item) => acc + item.st,
                          0
                        ),
                        tbh: filteredData?.dailyEntries?.reduce(
                          (acc, item) => acc + item.tbh,
                          0
                        ),
                        updatedBy: loginUserUiid,
                      },
                    ],
                  }),
                },
              },
            ],
            onSuccess: async (resData) => {
              setApproveModal(false);
              setRejectModal(false);
              setSelectedEmployeeIds([])
              dispatch(
                getTimesheetsStart({
                  page: page,
                  limit: limit,
                  filters: {
                    status: {
                      type: "IN",
                      value: [1, 2],
                    },
                  },
                  sort: { updatedAt: -1 },
                  ref: "Employee Client PlacementWithApprovers UpdatedBy",
                  incl: [
                    "employmentType firstName lastName",
                    "uiid businessName",
                    "uiid timesheetDetails.approvers",
                  ],
                })
              );
            },
          })
        );
      });
    }
  };

  return (
    <div className="flex-1 flex overflow-hidden">
      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
      <TableAdv
        rows={mappedRows}
        columns={columns}
        showCheckbox={true}
        onSelectionChange={handleSelectionChange}
        defaultSelectedRows={selectedEmployeeIds}
        serverPagination={true}
        toolbarRight={{
          filter: true,
          search: true,
          add: [
            {
              component: selectedEmployeeIds.length > 0 && (
                <div className="flex gap-2">
                  <Button
                    variant="contained"
                    size="sm"
                    title={"Approve"}
                    onClick={() => setApproveModal(true)}
                    color="black"
                    icon="person_add"
                    // disabled={true}
                  />
                  <Button
                    variant="contained"
                    size="sm"
                    title={"Reject"}
                    onClick={() => setRejectModal(true)}
                    color="black"
                    icon="person_remove"
                    // disabled={true}
                  />
                </div>
              ),
            },
          ],
        }}
        totalCount={totalCount?.data}
        page={page}
        limit={limit}
        onPaginationChange={setPage}
        onLimitChange={handleLimitChange}
        tableId="my-pending-timesheets-table"
      />
      <Modal
        isOpen={approveModal}
        onClose={() => setApproveModal(false)}
        title="Do you want to approve?"
        width="max-w-4xl"
      >
        <div>
          <div className="mb-20">
            <p>Are you sure you want to approve the selected timesheets?</p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outlined"
              title={"Cancel"}
              onClick={() => setApproveModal(false)}
            />
            <Button title={"OK"} onClick={() => handleSave(3)} />
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={rejectModal}
        onClose={() => setRejectModal(false)}
        title="Do you want to reject?"
        width="max-w-4xl"
      >
        <div>
          <div className="mb-20">
            <p>Are you sure you want to reject the selected timesheets?</p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outlined"
              title={"Cancel"}
              onClick={() => setRejectModal(false)}
            />
            <Button title={"OK"} onClick={() => handleSave(4)} />
          </div>
        </div>
      </Modal>
      <Modal
        width="max-w-2xl"
        title={"Document Details"}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <DocumentsList data={selectedDocuments} />
      </Modal>
    </div>
  );
};

export default MyPendingTimesheets;